import { Check } from "lucide-react";
import { Reveal } from "./Reveal";

const packs = [
  {
    id: "basico",
    name: "Básico",
    price: "300€",
    tagline: "Tu primera presencia online",
    delivery: "Entrega en 3-5 días",
    mockup: "https://static.prod-images.emergentagent.com/jobs/331fa164-8830-4533-b641-d510ecc6cd14/images/7800877ae423b3c93ef2fdf8f52ee204a781a65b46814cac28f859d9f45d1e3e.jpeg",
    mockupAlt: "Vista previa de una landing de una página diseñada por WebEssence para el Pack Básico",
    popular: false,
    features: [
      "Web de 1 sola página (landing)",
      "Diseño adaptado a móvil y ordenador",
      "Información del negocio: servicios, horarios, ubicación",
      "Mapa de Google integrado",
      "Dominio y hosting primer año incluido",
    ],
  },
  {
    id: "estandar",
    name: "Estándar",
    price: "400€",
    tagline: "El equilibrio perfecto",
    delivery: "Entrega en 5-7 días",
    mockup: "https://static.prod-images.emergentagent.com/jobs/331fa164-8830-4533-b641-d510ecc6cd14/images/4398b522752be738b874d3572cb103a82c729ef598d90d9a0a01094352cd1694.jpeg",
    mockupAlt: "Vista previa de una web de varias secciones diseñada por WebEssence para el Pack Estándar",
    popular: true,
    features: [
      "Web de hasta 4-5 páginas",
      "Todo lo del Pack Básico",
      "Formulario de contacto funcional",
      "Galería de fotos/productos",
      "SEO básico",
      "Conexión con redes sociales",
    ],
  },
  {
    id: "premium",
    name: "Premium",
    price: "500€",
    tagline: "Diseño a medida sin límites",
    delivery: "Entrega en 7-10 días",
    mockup: "https://static.prod-images.emergentagent.com/jobs/331fa164-8830-4533-b641-d510ecc6cd14/images/93a59b55bac31452942b5ab40d99efbf662e4ea2d97a238b73749a41f10e7390.jpeg",
    mockupAlt: "Vista previa de una web premium con diseño personalizado de WebEssence para el Pack Premium",
    popular: false,
    features: [
      "Todo lo del Pack Estándar",
      "Diseño 100% personalizado",
      "Sección de reseñas/testimonios",
      "Botón de reserva/cita online",
      "SEO avanzado",
      "1 mes de soporte post-entrega",
    ],
  },
];

const Mockup = ({ img, alt }) => (
  <div className="we-window mt-8">
    <div className="we-window-bar">
      <span className="we-dot bg-[#ff5f57]" />
      <span className="we-dot bg-[#febc2e]" />
      <span className="we-dot bg-[#28c840]" />
    </div>
    <img src={img} alt={alt} loading="lazy" className="h-40 w-full object-cover" />
  </div>
);

const CardBody = ({ pack }) => (
  <div className="flex h-full flex-col p-8">
    <div className="flex items-center justify-between">
      <h3 className="font-head text-2xl font-semibold text-white">{pack.name}</h3>
      {pack.popular && (
        <span className="rounded-full bg-[#00E5FF] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#0A0A0C]">
          Más popular
        </span>
      )}
    </div>
    <p className="mt-1 text-sm text-zinc-500">{pack.tagline}</p>
    <div className="mt-6 flex items-end gap-1">
      <span className="font-head text-5xl font-black text-white">{pack.price}</span>
    </div>
    <p className="mt-2 text-sm text-[#00E5FF]">{pack.delivery}</p>

    <ul className="mt-7 space-y-3">
      {pack.features.map((f) => (
        <li key={f} className="flex items-start gap-3 text-sm text-zinc-300">
          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#00E5FF]" />
          <span>{f}</span>
        </li>
      ))}
    </ul>

    <Mockup img={pack.mockup} alt={pack.mockupAlt} />

    <button
      data-testid={`pack-cta-${pack.id}`}
      onClick={() => document.getElementById("contacto")?.scrollIntoView({ behavior: "smooth" })}
      className={`mt-8 w-full rounded-full py-3.5 text-sm font-semibold transition-transform hover:-translate-y-0.5 ${
        pack.popular
          ? "bg-[#00E5FF] text-[#0A0A0C]"
          : "border border-white/20 text-white hover:bg-white/10"
      }`}
    >
      Elegir {pack.name}
    </button>
  </div>
);

export const Pricing = () => (
  <section id="packs" data-testid="pricing-section" className="relative mx-auto max-w-7xl px-5 py-24 md:px-10 md:py-32">
    <Reveal className="max-w-2xl">
      <span className="text-sm font-medium uppercase tracking-[0.25em] text-[#00E5FF]">02 — Nuestros packs</span>
      <h2 className="mt-5 font-head text-4xl font-light leading-tight tracking-tight text-white sm:text-5xl">
        Precios claros, <span className="font-black">sin sorpresas</span>
      </h2>
      <p className="mt-5 text-lg text-zinc-400">
        Elige el pack que mejor encaja con tu negocio. Todos incluyen diseño profesional y una web
        pensada para captar clientes reales.
      </p>
    </Reveal>

    <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-3">
      {packs.map((pack, i) => (
        <Reveal key={pack.id} delay={i * 0.1} data-testid={`pack-card-${pack.id}`}>
          {pack.popular ? (
            <div className="we-beam h-full lg:-mt-6 lg:mb-0">
              <CardBody pack={pack} />
            </div>
          ) : (
            <div className="h-full rounded-3xl border border-white/10 bg-[#101018] transition-colors hover:border-white/25">
              <CardBody pack={pack} />
            </div>
          )}
        </Reveal>
      ))}
    </div>
  </section>
);
